import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import Results from "@/pages/Results";
import { outcomeLabel, presetTargets, targetsFromLevels, levelsFromTargets } from "./resultPreview";

const CUSTOM = "__custom__";

/**
 * Shows the public result page for any result the instrument can produce, using the
 * editor's current state. The backend builds synthetic answers for the chosen result and
 * runs the real scoring engine; no session or response is stored.
 */
export default function ResultPreviewDialog({ open, onOpenChange, instrument, lang }) {
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selected, setSelected] = useState("");
  const [levels, setLevels] = useState({});
  const [lockedView, setLockedView] = useState(false);
  const [rendered, setRendered] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [showSliders, setShowSliders] = useState(false);
  // The editor stays behind a modal while it is open, so one snapshot per opening is enough.
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSnapshot(instrument);
    setOptions(null);
    setRendered(null);
    setError("");
    setLockedView(false);
    setLoadingOptions(true);
    api.post("/admin/result-preview/options", { instrument })
      .then(({ data }) => {
        setOptions(data);
        const first = data.outcomes?.[0];
        if (first) {
          setSelected(first.id);
          setLevels(levelsFromTargets(first.targets, data.dimensions));
        } else {
          setSelected(CUSTOM);
          setLevels(levelsFromTargets(presetTargets(data.dimensions, 0.5), data.dimensions));
        }
      })
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoadingOptions(false));
    // Only re-run when the dialog opens; `instrument` is captured in the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dimensions = options?.dimensions || [];
  const targets = useMemo(() => targetsFromLevels(levels), [levels]);

  useEffect(() => {
    if (!open || !snapshot || !options) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      setRendering(true);
      api.post("/admin/result-preview/render", {
        instrument: snapshot,
        targets,
        lang,
        paid_view: lockedView ? "locked" : "full",
      })
        .then(({ data }) => { if (active) { setRendered(data); setError(""); } })
        .catch((e) => { if (active) setError(formatApiError(e?.response?.data?.detail) || e.message); })
        .finally(() => { if (active) setRendering(false); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [open, snapshot, options, targets, lang, lockedView]);

  const preview = useMemo(
    () => (rendered ? { data: rendered, pdfMode: rendered.preview?.pdf_mode || { mode: "free" } } : null),
    [rendered],
  );

  const pickOutcome = (id) => {
    setSelected(id);
    const outcome = options?.outcomes?.find((o) => o.id === id);
    if (outcome) setLevels(levelsFromTargets(outcome.targets, dimensions));
  };

  const applyPreset = (level) => {
    setSelected(CUSTOM);
    setLevels(levelsFromTargets(presetTargets(dimensions, level), dimensions));
  };

  const setLevel = (dimId, value) => {
    setSelected(CUSTOM);
    setLevels((current) => ({ ...current, [dimId]: value }));
  };

  const result = rendered?.result;
  const outcomes = options?.outcomes || [];
  const unreached = options?.unreached_cards || [];
  const id = lang === "id";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[94vh] overflow-y-auto p-0 gap-0" data-testid="result-preview-dialog">
        <div className="sticky top-0 z-10 bg-background border-b border-border p-4 md:p-5 space-y-3">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{id ? "Preview Hasil" : "Result Preview"}</DialogTitle>
            <DialogDescription className="text-xs">
              {id
                ? "Pilih salah satu hasil untuk melihat halaman hasilnya persis seperti yang dilihat responden. Memakai isian editor saat ini, tidak ada jawaban yang disimpan."
                : "Pick a result to see its page exactly as a respondent would. Uses the editor's current state; no answers are saved."}
            </DialogDescription>
          </DialogHeader>

          {loadingOptions && (
            <div className="text-sm text-muted-foreground" data-testid="result-preview-loading">
              {id ? "Mencari semua kemungkinan hasil…" : "Finding every possible result…"}
            </div>
          )}

          {options && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[16rem] flex-1 max-w-xl">
                <Select value={selected} onValueChange={pickOutcome}>
                  <SelectTrigger data-testid="result-preview-select">
                    <SelectValue placeholder={id ? "Pilih hasil" : "Pick a result"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {outcomes.map((o) => (
                      <SelectItem key={o.id} value={o.id} data-testid={`result-preview-option-${o.id}`}>
                        {outcomeLabel(o, lang)}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM}>{id ? "Skor manual" : "Manual scores"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => applyPreset(0.1)}>{id ? "Semua rendah" : "All low"}</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => applyPreset(0.5)}>{id ? "Semua sedang" : "All mid"}</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => applyPreset(0.9)}>{id ? "Semua tinggi" : "All high"}</Button>
              {dimensions.length > 0 && (
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setShowSliders((v) => !v)} data-testid="result-preview-toggle-sliders">
                  {showSliders ? (id ? "Tutup skor manual" : "Hide manual scores") : (id ? "Atur skor manual" : "Adjust scores")}
                </Button>
              )}
              {rendered?.preview?.paid && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
                  <Switch checked={lockedView} onCheckedChange={setLockedView} data-testid="result-preview-locked-switch" />
                  {id ? "Tampilan sebelum bayar" : "Before payment view"}
                </label>
              )}
            </div>
          )}

          {options && showSliders && (
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 pt-1" data-testid="result-preview-sliders">
              {dimensions.map((d) => (
                <div key={d.id} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="truncate">{(id ? d.name_id : d.name_en) || d.name_id || d.code}</span>
                    <span className="tabular-nums text-muted-foreground">{levels[d.id] ?? 50}%</span>
                  </div>
                  <Slider min={0} max={100} step={1} value={[levels[d.id] ?? 50]} onValueChange={([v]) => setLevel(d.id, v)} />
                </div>
              ))}
            </div>
          )}

          {result && (
            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1" data-testid="result-preview-meta">
              <span>{id ? "Kunci hasil" : "Result key"}: <span className="font-mono text-foreground">{result.result_key || (id ? "belum terkonfigurasi" : "unresolved")}</span></span>
              <span>
                {id ? "Kartu dipakai" : "Card used"}: <span className="font-mono text-foreground">{result.result_mapping_entry?.result_key || "—"}</span>
                {result.result_mapping_resolved_from && result.result_mapping_resolved_from !== "exact" && ` (${result.result_mapping_resolved_from})`}
              </span>
              {rendering && <span>{id ? "Memperbarui…" : "Updating…"}</span>}
            </div>
          )}

          {unreached.length > 0 && (
            <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2" data-testid="result-preview-unreached">
              {id
                ? "Kartu hasil ini belum muncul dari kombinasi skor mana pun yang dicoba. Cek lagi kunci hasilnya, atau coba atur skor manual: "
                : "These result cards never came up in any score combination tried. Check their result keys, or try manual scores: "}
              <span className="font-mono">{unreached.map((c) => c.result_key).join(", ")}</span>
            </div>
          )}

          {error && <div className="text-xs text-destructive" data-testid="result-preview-error">{error}</div>}
        </div>

        <div className="bg-muted/30 p-2 md:p-4">
          <div className="rounded-2xl border border-border bg-background overflow-hidden" data-testid="result-preview-frame">
            {preview ? <Results preview={preview} /> : (
              <div className="py-20 text-center text-sm text-muted-foreground">…</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
